import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper/bundle';

gsap.registerPlugin(ScrollTrigger);

const SELECTION_BREAKPOINT = 768; // middle-screen

const barcinoHome = {

    _listeners: [],
    _selectionSwiper: null,
    _selectionStackContext: null,
    _categoriesSwiper: null,
    _coffretsSwiper: null,
    _stackItems: [],        // items absolus créés par initSelectionStack

    init() {
        this.initSelectionSwiper();
        this.initCategoriesSwiper();
        this.initCoffretsSwiper();
        this.initSelectionWeights();
        this.initSelectionStack();
        this.initReveal();

        const onResize = () => {
            this.initSelectionSwiper();
            this.initSelectionStack();
            this.initCategoriesSwiper();
            this.initCoffretsSwiper();
            // Recalcule le stack si on change de breakpoint
            ScrollTrigger.refresh();
        };
        window.addEventListener('resize', onResize, { passive: true });
        this._listeners.push({ el: window, type: 'resize', fn: onResize });
    },

    initCategoriesSwiper() {
        const el = document.querySelector('[data-categories-swiper]');
        if (!el || this._categoriesSwiper) return;

        this._categoriesSwiper = new Swiper(el, {
            slidesPerView: 2.15,
            spaceBetween: 8,
            grabCursor: true,
            // slideRole vide : le markup est un <ul>/<li> natif, le role="group"
            // par défaut de Swiper casse la sémantique de liste pour les lecteurs
            // d'écran (audit Lighthouse "list").
            a11y: { slideRole: '' },
            // Recalcule la largeur des slides si le conteneur est mesuré à 0 au
            // moment de l'init (entrée Barba, pane masqué…) puis redimensionné.
            observer: true,
            observeParents: true,
            navigation: {
                prevEl: '.bc-categories__prev',
                nextEl: '.bc-categories__next',
            },
            breakpoints: {
                // ~4,2 slides visibles : 4 pleines + un aperçu de la suivante
                // qui signale qu'on peut swiper.
                769: { slidesPerView: 4.2, spaceBetween: 12 },
            },
        });
    },

    // ─── Sélection : boutons poids → sync data-cart-add + prix ─────────────
    initSelectionWeights() {
        const section = document.querySelector('.bc-selection');
        if (!section) return;

        const onWeightClick = (e) => {
            const btn = e.target.closest('.bc-selection__weight-btn');
            if (!btn) return;

            const item     = btn.closest('.bc-selection__item');
            const addBtn   = item?.querySelector('[data-cart-add]');
            const priceEl  = item?.querySelector('.bc-selection__price');
            const oldPriceEl = item?.querySelector('[data-selection-price-old]');
            const discountEl = item?.querySelector('[data-selection-discount]');
            const kiloEl   = item?.querySelector('[data-selection-price-kilo]');

            // Désélectionne les autres poids du même item
            item?.querySelectorAll('.bc-selection__weight-btn').forEach(b => b.classList.remove('is-active'));
            btn.classList.add('is-active');

            // Met à jour le variant cible du bouton "Ajouter au panier"
            if (addBtn) {
                const stock = Number.parseInt(btn.dataset.stock || '0', 10);
                addBtn.dataset.cartAdd = btn.dataset.variantId || '';
                addBtn.disabled = stock < 1;
                addBtn.textContent = stock < 1 ? addBtn.dataset.labelOut : addBtn.dataset.labelAdd;
            }

            // Met à jour le prix affiché
            if (priceEl && btn.dataset.price) {
                const price = parseFloat(btn.dataset.price);
                const currency = priceEl.dataset.priceCurrency || '';
                const priceLocale = document.documentElement.lang === 'en' ? 'en-GB' : 'fr-FR';
                priceEl.textContent = new Intl.NumberFormat(priceLocale, {
                    minimumFractionDigits: 2, maximumFractionDigits: 2,
                }).format(price) + currency;

                if (oldPriceEl) {
                    const oldPrice = parseFloat(btn.dataset.oldPrice || '');
                    oldPriceEl.textContent = Number.isFinite(oldPrice)
                        ? new Intl.NumberFormat(priceLocale, {
                            minimumFractionDigits: 2, maximumFractionDigits: 2,
                        }).format(oldPrice) + currency
                        : '';
                    oldPriceEl.hidden = !Number.isFinite(oldPrice);
                }
                if (discountEl) {
                    discountEl.textContent = btn.dataset.discountLabel || '';
                    discountEl.hidden = !btn.dataset.discountLabel;
                }

                if (kiloEl) {
                    const grams = parseFloat(btn.dataset.weightGrams || '0');
                    const suffix = kiloEl.dataset.priceKiloSuffix || '';
                    kiloEl.textContent = grams > 0
                        ? new Intl.NumberFormat(priceLocale, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(price / (grams / 1000)) + currency + suffix
                        : '';
                }
            }
        };

        section.addEventListener('click', onWeightClick);
        this._listeners.push({ el: section, type: 'click', fn: onWeightClick });
    },

    // ─── Swiper sélection du mois — mobile uniquement ───────────────────────
    // Le HTML ne contient aucune classe Swiper.
    // Sur mobile : JS ajoute swiper/swiper-wrapper/swiper-slide.
    // Sur desktop/destroy : JS retire tout et restaure le DOM propre.
    initSelectionSwiper() {
        const el = document.querySelector('[data-selection-swiper]');
        if (!el) return;

        const isMobile = window.innerWidth <= SELECTION_BREAKPOINT;

        // Libère le pin et les styles du desktop avant de construire le Swiper.
        if (isMobile && this._selectionStackContext) {
            this._selectionStackContext.revert();
            this._selectionStackContext = null;
            this._stackItems = [];
        }

        if (isMobile && !this._selectionSwiper) {
            // 1. Enveloppe les items dans un swiper-wrapper
            const items = Array.from(el.querySelectorAll('.bc-selection__item'));
            const wrapper = document.createElement('div');
            wrapper.className = 'swiper-wrapper';
            items.forEach(item => {
                item.classList.add('swiper-slide');
                wrapper.appendChild(item);
            });
            el.appendChild(wrapper);

            // 2. Active Swiper
            el.classList.add('swiper');
            this._selectionSwiper = new Swiper(el, {
                slidesPerView: 'auto',
                spaceBetween: 16,
                grabCursor: true,
                autoHeight: true,
                a11y: { slideRole: '' },
                navigation: {
                    prevEl: document.querySelector('[data-selection-previous]'),
                    nextEl: document.querySelector('[data-selection-next]'),
                },
            });

        } else if (!isMobile && this._selectionSwiper) {
            this._selectionSwiper.destroy(true, true);
            this._selectionSwiper = null;

            // Restaure le DOM : sort les items du wrapper, retire les classes Swiper
            const wrapper = el.querySelector('.swiper-wrapper');
            if (wrapper) {
                Array.from(wrapper.children).forEach(item => {
                    item.classList.remove('swiper-slide');
                    el.insertBefore(item, wrapper);
                });
                wrapper.remove();
            }
            el.classList.remove('swiper', 'swiper-initialized');
        }
    },

    // ─── Reveal au scroll (GSAP ScrollTrigger) ───────────────────────────────
    initCoffretsSwiper() {
        const el = document.querySelector('[data-coffrets-swiper]');
        if (!el || this._coffretsSwiper) return;

        const cards = Array.from(el.querySelectorAll('.bc-catalogue-card'));
        if (!cards.length) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';
        cards.forEach(card => {
            card.classList.add('swiper-slide');
            wrapper.appendChild(card);
        });
        el.appendChild(wrapper);
        el.classList.add('swiper');

        this._coffretsSwiper = new Swiper(el, {
            slidesPerView: 1.08,
            spaceBetween: 16,
            grabCursor: true,
            a11y: { slideRole: '' },
            navigation: {
                prevEl: '.bc-coffrets__prev',
                nextEl: '.bc-coffrets__next',
            },
            breakpoints: {
                // Tablette : deux coffrets complets et un aperçu du suivant.
                601: {
                    slidesPerView: 2.1,
                    spaceBetween: 16,
                },
                769: {
                    slidesPerView: 2,
                    spaceBetween: 16,
                },
                1081: {
                    slidesPerView: 3,
                    spaceBetween: 16,
                },
            },
        });
    },

    destroyCoffretsSwiper() {
        if (this._coffretsSwiper) {
            this._coffretsSwiper.destroy(true, true);
            this._coffretsSwiper = null;
        }

        const el = document.querySelector('[data-coffrets-swiper]');
        if (!el) return;

        const wrapper = el.querySelector('.swiper-wrapper');
        if (wrapper) {
            Array.from(wrapper.children).forEach(card => {
                card.classList.remove('swiper-slide');
                el.insertBefore(card, wrapper);
            });
            wrapper.remove();
        }
        el.classList.remove('swiper', 'swiper-initialized');
    },

    initReveal() {
        const els = document.querySelectorAll(
            '.hero__title, .hero__subtitle, .hero__text, .hero__cta, ' +
            '.selection-mois__header, .product-card, ' +
            '.vision-editoriale__text, .vision-editoriale__image, ' +
            '.torreFfacteur-highlight__content, ' +
            '.coffrets__header, .coffret-card, ' +
            '.newsletter__title'
        );

        els.forEach((el, i) => {
            gsap.from(el, {
                opacity: 0,
                y: 40,
                duration: 0.8,
                delay: (i % 3) * 0.1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 88%',
                    once: true,
                },
            });
        });
    },

    // ─── Sélection : stacking scrollmagic desktop ───────────────────────────
    // On pine uniquement la __list (pas toute la section).
    // Le titre "Sélection du mois" défile normalement au-dessus.
    // Les items sont dimensionnés à (100vh - hauteur header) pour tout voir.
    initSelectionStack() {
        if (window.innerWidth <= SELECTION_BREAKPOINT || this._selectionStackContext) return;

        const list  = document.querySelector('.bc-selection__list');
        const items = Array.from(document.querySelectorAll('.bc-selection__item'));
        if (!list || items.length < 2) return;

        // Le contexte restaure la mise en page initiale à la sortie du desktop.
        this._selectionStackContext = gsap.context(() => {
            // Hauteur disponible sous le header fixe
            const headerH = document.getElementById('bc-header')?.offsetHeight ?? 80;
            const availH  = window.innerHeight - headerH;

            // Hauteur de la liste = une carte = espace disponible
            gsap.set(list, { position: 'relative', height: availH });

            // Item 1 : remplit la hauteur disponible
            gsap.set(items[0], { height: availH });

            const imageBlocks = items
                .map(item => item.querySelector('.bc-selection__img-wrap'))
                .filter(Boolean);

            gsap.set(imageBlocks, {
                scale: 1,
                transformOrigin: 'center center',
                willChange: 'transform',
            });

            // Items 2+ : superposés sur l'item 1, partent du bas du viewport
            const stackItems = items.slice(1);
            stackItems.forEach((item, i) => {
                gsap.set(item, {
                    position: 'absolute',
                    top:      0,
                    left:     0,
                    right:    0,
                    height:   availH,
                    y:        availH,
                    zIndex:   i + 2,
                });
            });
            this._stackItems = stackItems;

            // Timeline scrubée — trigger = __list, pin sous le header
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger:       list,
                    start:         `top ${headerH}px`,
                    end:           () => `+=${stackItems.length * window.innerHeight}`,
                    scrub:         1,
                    pin:           true,
                    anticipatePin: 1,
                },
            });

            stackItems.forEach((item, i) => {
                const previousImage = items[i]?.querySelector('.bc-selection__img-wrap');
                const currentImage = item.querySelector('.bc-selection__img-wrap');

                if (previousImage) {
                    tl.to(previousImage, {
                        scale: 0.92,
                        ease: 'power1.out',
                        duration: 1,
                    }, i);
                }

                tl.to(item, { y: 0, ease: 'none', duration: 1 }, i);

                if (currentImage) {
                    tl.fromTo(currentImage, {
                        scale: 1.04,
                    }, {
                        scale: 1,
                        ease: 'power1.out',
                        duration: 1,
                    }, i);
                }

                tl.to(items[i], { opacity: 0, ease: 'power1.in', duration: 0.5 }, i + 0.5);
            });
        });
    },

    destroy() {
        if (this._selectionStackContext) {
            this._selectionStackContext.revert();
            this._selectionStackContext = null;
            this._stackItems = [];
        }
        ScrollTrigger.getAll().forEach(t => t.kill());

        // Nettoie les styles inline posés par initSelectionStack
        if (this._stackItems.length) {
            const allItems = Array.from(document.querySelectorAll('.bc-selection__item'));
            gsap.set(allItems, { clearProps: 'all' });
            gsap.set('.bc-selection__img-wrap', { clearProps: 'transform,transformOrigin,willChange' });
            this._stackItems = [];
        }
        const list = document.querySelector('.bc-selection__list');
        if (list) gsap.set(list, { clearProps: 'all' });

        this._listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._listeners = [];
        if (this._selectionSwiper) {
            this._selectionSwiper.destroy(true, true);
            this._selectionSwiper = null;
            const el = document.querySelector('[data-selection-swiper]');
            if (el) {
                const wrapper = el.querySelector('.swiper-wrapper');
                if (wrapper) {
                    Array.from(wrapper.children).forEach(item => {
                        item.classList.remove('swiper-slide');
                        el.insertBefore(item, wrapper);
                    });
                    wrapper.remove();
                }
                el.classList.remove('swiper', 'swiper-initialized');
            }
        }
        this.destroyCoffretsSwiper();
        if (this._categoriesSwiper) {
            this._categoriesSwiper.destroy(true, true);
            this._categoriesSwiper = null;
        }
    },
};

export default barcinoHome;
